import React, { useState, useEffect, useRef } from 'react';import { useNavigate } from 'react-router-dom';import API_BASE_URL from '../config';import styles from '../css-isolated/notification.module.css';
import authFetch from '../utils/authFetch';

function NotificationBell() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overdue');
  const [readIds, setReadIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('notif_read_ids') || '[]');
    } catch {
      return [];
    }
  });
  const dropdownRef = useRef(null);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await authFetch(`${API_BASE_URL}/api/followup-consult-upcoming`);
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setNotifications(data);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // refresh every minute
    return () => clearInterval(interval);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);


  const markAllRead = () => {
    const allIds = notifications.map((n) => n.id);
    const updated = [...new Set([...readIds, ...allIds])];
    setReadIds(updated);
    localStorage.setItem('notif_read_ids', JSON.stringify(updated));
  };

  const markOneRead = (id) => {
    if (readIds.includes(id)) return;
    const updated = [...readIds, id];
    setReadIds(updated);
    localStorage.setItem('notif_read_ids', JSON.stringify(updated));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-PH', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(dateStr);
    target.setHours(0, 0, 0, 0);
    const diff = Math.round((target - today) / (1000 * 60 * 60 * 24));
    if (diff < 0) return { label: `${Math.abs(diff)} day${Math.abs(diff) !== 1 ? 's' : ''} overdue`, urgency: 'overdue' };
    if (diff === 0) return { label: 'Today', urgency: 'today' };
    if (diff === 1) return { label: 'Tomorrow', urgency: 'soon' };
    if (diff <= 3) return { label: `In ${diff} days`, urgency: 'soon' };
    return { label: `In ${diff} days`, urgency: 'normal' };
  };

  const getPatientName = (notif) => {
    const info = notif.hr_info;
    if (!info) return `PDL #${notif.hr_info_id}`;
    const last = info.last_name || '';
    const first = info.first_name || '';
    const middle = info.middle_name ? ` ${info.middle_name.charAt(0)}.` : '';
    return `${last}, ${first}${middle}`.trim() || `PDL #${notif.hr_info_id}`;
  };

  const getCellName = (notif) => {
    return notif.hr_info?.cell?.cell_name || notif.hr_info?.cell?.name || null;
  };

  // Group notifications by tab
  const overdueNotifs   = notifications.filter((n) => getDaysUntil(n.date)?.urgency === 'overdue');
  const todayNotifs     = notifications.filter((n) => getDaysUntil(n.date)?.urgency === 'today');
  const upcomingNotifs  = notifications.filter((n) => {
    const u = getDaysUntil(n.date)?.urgency;
    return u === 'soon' || u === 'normal';
  });

  // Bell colour priority: overdue > today > any unread > idle
  const unreadCount = notifications.filter((n) => !readIds.includes(n.id)).length;
  const bellStatus = overdueNotifs.length > 0 ? 'overdue'
                   : todayNotifs.length   > 0 ? 'today'
                   : unreadCount          > 0 ? 'active'
                   : 'idle';

  const tabMap = { overdue: overdueNotifs, today: todayNotifs, upcoming: upcomingNotifs };
  const visibleNotifs = tabMap[activeTab] || [];

  const TABS = [
    { key: 'overdue',  label: 'Overdue',  count: overdueNotifs.length },
    { key: 'today',    label: 'Today',    count: todayNotifs.length },
    { key: 'upcoming', label: 'Upcoming', count: upcomingNotifs.length },
  ];

  // Render a single notification list item
  const renderItem = (notif) => {
    const daysInfo = getDaysUntil(notif.date);
    const isRead = readIds.includes(notif.id);
    const cell = getCellName(notif);
    const handleItemClick = () => {
      markOneRead(notif.id);
      setIsOpen(false);
      navigate('/health-consultation', { state: { initialView: 'followup', followupDate: notif.date } });
    };
    return (
      <li
        key={notif.id}
        className={`${styles.notifItem} ${!isRead ? styles.unread : ''} ${
          daysInfo?.urgency === 'overdue' ? styles.overdue :
          daysInfo?.urgency === 'today'   ? styles.today   : ''
        }`}
        onClick={() => handleItemClick()}
      >
        <div className={styles.notifIcon}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <line x1="8"  y1="2" x2="8"  y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <line x1="3"  y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div className={styles.notifContent}>
          <div className={styles.notifName}>{getPatientName(notif)}</div>
          <div className={styles.notifMeta}>
            {cell && <span className={styles.cellTag}>{cell}</span>}
            <span className={styles.notifDate}>{formatDate(notif.date)}</span>
          </div>
          {daysInfo && (
            <span className={`${styles.urgencyTag} ${
              daysInfo.urgency === 'overdue' ? styles.tagOverdue :
              daysInfo.urgency === 'today'   ? styles.tagToday   :
              daysInfo.urgency === 'soon'    ? styles.tagSoon    : styles.tagNormal
            }`}>
              {daysInfo.label}
            </span>
          )}
        </div>
        {!isRead && <div className={styles.unreadDot}></div>}
      </li>
    );
  };

  return (
    <div className={styles.notifWrapper} ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        className={styles.bellBtn}
        onClick={() => setIsOpen((prev) => !prev)}
        title="Follow-up Consultations"
        aria-label="Notifications"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={
            bellStatus === 'overdue' ? styles.bellIconOverdue :
            bellStatus === 'today'   ? styles.bellIconToday   :
            bellStatus === 'active'  ? styles.bellIconActive  :
            styles.bellIcon
          }
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {unreadCount > 0 && (
          <span className={`${styles.badge} ${
            bellStatus === 'overdue' ? styles.badgeOverdue :
            bellStatus === 'today'   ? styles.badgeToday   : ''
          }`}>{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className={styles.dropdown}>
          {/* Header */}
          <div className={styles.dropdownHeader}>
            <div className={styles.dropdownTitle}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Follow-up Consultations
            </div>
            {unreadCount > 0 && (
              <button className={styles.markAllBtn} onClick={markAllRead}>
                Mark all read
              </button>
            )}
          </div>

          {/* Tab Bar */}
          <div className={styles.tabBar}>
            {TABS.map((tab) => (
              <button
                key={tab.key}
                className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''} ${
                  tab.key === 'overdue' ? styles.tabOverdueKey :
                  tab.key === 'today'   ? styles.tabTodayKey   : styles.tabUpcomingKey
                }`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className={`${styles.tabBadge} ${
                    tab.key === 'overdue' ? styles.tabBadgeOverdue :
                    tab.key === 'today'   ? styles.tabBadgeToday   : styles.tabBadgeUpcoming
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Body */}
          <div className={styles.dropdownBody}>
            {loading ? (
              <div className={styles.emptyState}>
                <div className={styles.loadingSpinner}></div>
                <span>Loading...</span>
              </div>
            ) : visibleNotifs.length === 0 ? (
              <div className={styles.emptyState}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <polyline points="22 4 12 14.01 9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>
                  {activeTab === 'overdue'  ? 'No overdue consultations' :
                   activeTab === 'today'    ? 'No consultations today'   :
                                             'No upcoming consultations'}
                </span>
              </div>
            ) : (
              <ul className={styles.notifList}>
                {visibleNotifs.map(renderItem)}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className={styles.dropdownFooter}>
            <span>
              {visibleNotifs.length} {activeTab} follow-up{visibleNotifs.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;

